import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import type { StaffManagementData, DailyOperationsData, CustomerFeedbackData } from '../types';

export interface ManagerReportsState {
  staffManagement: StaffManagementData | null;
  dailyOperations: DailyOperationsData | null;
  customerFeedback: CustomerFeedbackData | null;
  loading: {
    staffManagement: boolean;
    dailyOperations: boolean;
    customerFeedback: boolean;
  };
  error: string | null;
}

const initialState: ManagerReportsState = {
  staffManagement: null,
  dailyOperations: null,
  customerFeedback: null,
  loading: {
    staffManagement: false,
    dailyOperations: false,
    customerFeedback: false,
  },
  error: null,
};

// Async thunks for fetching report data
export const fetchStaffManagementReport = createAsyncThunk(
  'managerReports/fetchStaffManagement',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/manager/reports/staff-management');
      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch staff management report'
      );
    }
  }
);

export const fetchDailyOperationsReport = createAsyncThunk(
  'managerReports/fetchDailyOperations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/manager/reports/daily-operations');
      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch daily operations report'
      );
    }
  }
);

export const fetchCustomerFeedbackReport = createAsyncThunk(
  'managerReports/fetchCustomerFeedback',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/manager/reports/customer-feedback');
      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch customer feedback report'
      );
    }
  }
);

const managerReportsSlice = createSlice({
  name: 'managerReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetReports: (state) => {
      state.staffManagement = null;
      state.dailyOperations = null;
      state.customerFeedback = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Staff Management
    builder
      .addCase(fetchStaffManagementReport.pending, (state) => {
        state.loading.staffManagement = true;
        state.error = null;
      })
      .addCase(fetchStaffManagementReport.fulfilled, (state, action) => {
        state.loading.staffManagement = false;
        state.staffManagement = action.payload;
      })
      .addCase(fetchStaffManagementReport.rejected, (state, action) => {
        state.loading.staffManagement = false;
        state.error = action.payload as string;
      });

    // Daily Operations
    builder
      .addCase(fetchDailyOperationsReport.pending, (state) => {
        state.loading.dailyOperations = true;
        state.error = null;
      })
      .addCase(fetchDailyOperationsReport.fulfilled, (state, action) => {
        state.loading.dailyOperations = false;
        state.dailyOperations = action.payload;
      })
      .addCase(fetchDailyOperationsReport.rejected, (state, action) => {
        state.loading.dailyOperations = false;
        state.error = action.payload as string;
      });

    // Customer Feedback
    builder
      .addCase(fetchCustomerFeedbackReport.pending, (state) => {
        state.loading.customerFeedback = true;
        state.error = null;
      })
      .addCase(fetchCustomerFeedbackReport.fulfilled, (state, action) => {
        state.loading.customerFeedback = false;
        state.customerFeedback = action.payload;
      })
      .addCase(fetchCustomerFeedbackReport.rejected, (state, action) => {
        state.loading.customerFeedback = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetReports } = managerReportsSlice.actions;
export default managerReportsSlice.reducer;
